import { Injectable, inject } from '@angular/core';
import { DateTime } from 'luxon';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { IBusinessYear } from '../interfaces/dates/IBusinessYear';
import { IPeriod } from '../interfaces/IPeriod';
import { MONTHS } from '../enums/Months.enum';
import { MONTHS_DE } from '../enums/Months.enum';
import { DateTransformOperations } from '../operations/date-transform.operations';
import { IEmployeeBase } from '../interfaces/IEmployee';
import { IEmployeeListItem } from '../interfaces/IEmployeeListItem';
import { USER_ROLES } from '../enums/UserRoles.enum';
import { BILLING_INFORMATION_TYPE } from '../enums/BillingInformation.enum';

@Injectable({
  providedIn: 'root',
})
export class DateService {
  private dateTransformOperations = inject(DateTransformOperations);
  private translateService = inject(TranslateService);
  private _accountingMonth: string;

  // TO DO? refactor this to make use of public static methods instead of having to pass this utility service everywhere
  public now = new Date();
  public today = new Date(this.now.getFullYear(), this.now.getMonth(), this.now.getDate());
  public displayedMonths: IPeriod[] = [];
  public dateFormat = 'yyyy-MM-dd';
  public monthsDE: MONTHS_DE[] = Object.values(MONTHS_DE);
  public monthsEN: MONTHS[] = Object.values(MONTHS);
  public accountingMonthObservable: Observable<string> = of(localStorage.getItem('accountingMonth'));

  public getBusinessYear(date: Date, separator: string): string {
    const dates = this.getBusinessYearRange(date);
    return [dates[0].getFullYear().toString().substring(2, 4), dates[1].getFullYear().toString().substring(2, 4)].join(separator || '');
  }

  public getBusinessYearRange(date: Date): Date[] {
    const currentMonth = date.getMonth();
    const rangeDate1 = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const rangeDate2 = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

    if (currentMonth < 9) {
      rangeDate1.setMonth(rangeDate1.getMonth() - 12);
    } else {
      rangeDate2.setMonth(rangeDate2.getMonth() + 12);
    }

    return [new Date(Date.UTC(rangeDate1.getFullYear(), 9, 1)), new Date(Date.UTC(rangeDate2.getFullYear(), 8, 30))];
  }

  getFirstDayOfCurrentMonth(): Date {
    const date = new Date();
    date.setDate(1);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  public getCurrentFiscalYearEndDate(): Date {
    const currentBusinessYearRange = this.getBusinessYearRange(this.today);
    return currentBusinessYearRange[1];
  }

  public getBusinessYears(): IBusinessYear[] {
    const currentBusinessYearRange = this.getBusinessYearRange(this.today);
    const currentBusinessYearStart = currentBusinessYearRange[0];
    const previousYear = new Date(
      currentBusinessYearStart.getFullYear() - 1,
      currentBusinessYearStart.getMonth(),
      currentBusinessYearStart.getDate(),
    );
    const nextYear = new Date(
      currentBusinessYearStart.getFullYear() + 1,
      currentBusinessYearStart.getMonth(),
      currentBusinessYearStart.getDate(),
    );
    const currentBusinessYear = this.getBusinessYear(this.today, '/');
    const previousBusinessYear = this.getBusinessYear(previousYear, '/');
    const nextBusinessYear = this.getBusinessYear(nextYear, '/');

    return [
      {
        apiTerm: previousBusinessYear.replace(/\//gi, '%2F'),
        date: previousYear,
        label: previousBusinessYear,
      },
      {
        apiTerm: currentBusinessYear.replace(/\//gi, '%2F'),
        date: currentBusinessYearStart,
        label: currentBusinessYear,
      },
      {
        apiTerm: nextBusinessYear.replace(/\//gi, '%2F'),
        date: nextYear,
        label: nextBusinessYear,
      },
    ];
  }

  private getBusinessYearsForTechnicalController(): IBusinessYear[] {
    const currentBusinessYearRange = this.getBusinessYearRange(this.today);
    const currentBusinessYearStart = currentBusinessYearRange[0];
    const years: IBusinessYear[] = [];
    const startYear = 2018;
    const endYear = currentBusinessYearStart.getFullYear() + 1;
    for (let year = startYear; year <= endYear; year++) {
      const yearStart = new Date(year, currentBusinessYearStart.getMonth(), currentBusinessYearStart.getDate());
      const businessYear = this.getBusinessYear(yearStart, '/');
      years.push({
        apiTerm: businessYear.replace(/\//gi, '%2F'),
        date: yearStart,
        label: businessYear,
      });
    }
    return years;
  }

  public getBusinessYearsForUserRole(userRole: USER_ROLES): IBusinessYear[] {
    if (userRole === USER_ROLES.TECHNISCHER_CONTROLLER) {
      return this.getBusinessYearsForTechnicalController();
    }
    return this.getBusinessYears();
  }

  public getBusinessYearsAsArray(): number[] {
    const currentYear = this.today.getFullYear();
    let years = [];

    years = [currentYear - 1, currentYear, currentYear + 1];
    return years;
  }

  public set accountingMonth(value: string) {
    this._accountingMonth = value;
  }

  public get accountingMonthNumber(): number {
    return new Date(this._accountingMonth).getMonth() + 1;
  }

  public getMonth(months?: IPeriod[]): IPeriod {
    const displayedMonth = localStorage.getItem('accountingMonth');
    const date: Date = displayedMonth
      ? this.dateTransformOperations.convertToUTCDate(displayedMonth, 'last')
      : DateTime.now().endOf('month').toJSDate();
    return this.findMonth(displayedMonth, date, months);
  }

  public generateDisplayedMonths(): IPeriod[] {
    if (this.displayedMonths.length) {
      return this.displayedMonths;
    }
    const date = new Date();
    const result = [];
    let count = 0;
    let year = date.getFullYear() - 1;
    let start = date.getMonth();
    while (start < 12 && count !== 26) {
      const element: IPeriod = new IPeriod();
      element.month = start;
      element.year = year;
      element.date = new Date(
        DateTime.fromJSDate(new Date(year, start, 1, 0, 0, 0, 0))
          .startOf('month')
          .toString(),
      );
      element.periodStart = new Date(
        DateTime.fromJSDate(new Date(year, start, 1, 0, 0, 0, 0))
          .startOf('month')
          .toString(),
      );
      element.periodEnd = new Date(
        DateTime.fromJSDate(new Date(year, start, 1, 0, 0, 0, 0))
          .endOf('month')
          .toString(),
      );
      element.titleDE = `${this.monthsDE[start]} ${year}`;
      element.titleEN = `${this.monthsEN[start]} ${year}`;
      element.value = `${start + 1}-${year}`;
      result.push(element);
      count++;
      start++;
      if (count === 26) {
        this.displayedMonths = result;
      } else if (start === 12) {
        start = 0;
        year++;
      }
    }
    return this.displayedMonths;
  }

  public generateDaysForCalendar(month: IPeriod): Date[] {
    const days = [];
    let date = month.periodStart;
    while (this.formatDate(date) <= this.formatDate(month.periodEnd)) {
      days.push(this.toDateWithoutTimeZone(DateTime.fromJSDate(date).toJSDate()));
      date = this.toDateWithoutTimeZone(DateTime.fromJSDate(date).plus({ days: 1 }).toJSDate());
    }
    return days;
  }

  public formatDate(date: Date | string): string {
    return typeof date === 'string'
      ? DateTime.fromISO(date).toFormat(this.dateFormat)
      : DateTime.fromJSDate(date).toFormat(this.dateFormat);
  }

  public getDayNumberInWeek(date: Date): number {
    return DateTime.fromJSDate(date).weekday;
  }

  public isWeekend(date: Date | string): boolean {
    const dateTime = typeof date === 'string' ? DateTime.fromISO(date) : DateTime.fromJSDate(date);

    return dateTime.weekday === 6 || dateTime.weekday === 7;
  }

  public getDay(date: Date): number {
    return DateTime.fromJSDate(date).day;
  }

  public datesEqual(date1: Date | string, date2: Date | string) {
    return this.formatDate(date1) === this.formatDate(date2);
  }

  public toIsoStringWithoutTimeZone(initialDate: Date): string {
    const dateTime = DateTime.fromJSDate(initialDate).startOf('day');
    return dateTime.toISO().split('T')[0];
  }

  public toDateWithoutTimeZone(initialDate: Date): Date {
    const date = new Date(Date.UTC(initialDate.getFullYear(), initialDate.getMonth(), initialDate.getDate()));
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  }

  public getTime(date: Date | string): string {
    const dateTime = typeof date === 'string' ? DateTime.fromISO(date) : DateTime.fromJSDate(date);
    return dateTime.toFormat('HH:mm');
  }

  public canHoursBeApproved(month: Date, employee: IEmployeeListItem | IEmployeeBase): boolean {
    return this.formatDate(employee.lastApprovedMonth) < this.formatDate(month);
  }

  public getLastDayOfPreviousMonthAsISODate(month: Date): string {
    return DateTime.fromJSDate(new Date(month.getFullYear(), month.getMonth(), 0)).toISODate();
  }

  public getLastDayOfMonthAsISODate(month: Date): string {
    return DateTime.fromJSDate(new Date(month.getFullYear(), month.getMonth() + 1, 0)).toISODate();
  }
  public getFirstDayOfMonthAsISODate(month: Date): string {
    return DateTime.fromJSDate(new Date(month.getFullYear(), month.getMonth(), 1)).toISODate();
  }

  public getMonthDifference(date1: Date, date2: Date): number {
    return (
      (new Date(date2).getFullYear() - new Date(date1).getFullYear()) * 12 + (new Date(date2).getMonth() - new Date(date1).getMonth()) + 1
    );
  }

  public getCurrentFiscalYear(displayedFY: IBusinessYear[]): IBusinessYear {
    const today = new Date();
    return (
      displayedFY.find(
        fy =>
          fy.date.getFullYear() === today.getFullYear() && fy.date.getMonth() === today.getMonth() && fy.date.getDate() === today.getDate(),
      ) || displayedFY[Math.max(0, displayedFY.length - 2)]
    );
  }

  public getMonthString(monthNumber: number): string {
    return this.translateService.getCurrentLang() === 'de' ? this.monthsDE[monthNumber] : this.monthsEN[monthNumber];
  }

  public getNoBillingMonthsTranslatedString(billingMonthsKey: BILLING_INFORMATION_TYPE): string {
    switch (billingMonthsKey) {
      case BILLING_INFORMATION_TYPE.BILLED_MONTHS:
        return this.translateService.instant('account.billingInformation.noBilledMonthsYet');
      case BILLING_INFORMATION_TYPE.BILLING_MONTHS:
        return this.translateService.instant('account.billingInformation.noBillingMonthsYet');
      case BILLING_INFORMATION_TYPE.BILLING_ACTUAL_MONTH:
        return this.translateService.instant('account.billingInformation.noBillingActualMonthYet');
      default:
        return '';
    }
  }

  public getDateFromISOString(isoString: string): Date {
    const [year, month, day, hour, minute, second] = isoString.split(/\D+/).map(Number);
    return new Date(year, month - 1, day, hour, minute, second);
  }

  public getAccountingMonth(): Date {
    let accountingMonth = new Date();
    const accountingMonthStorage = localStorage.getItem('accountingMonth');
    if (accountingMonthStorage) {
      accountingMonth = new Date(accountingMonthStorage);
    }
    return accountingMonth;
  }

  private findMonth(displayedMonth: string, date: Date, months?: IPeriod[]) {
    if (!months) {
      months = this.generateDisplayedMonths();
    }
    for (const month of months) {
      if (
        month.value === displayedMonth ||
        (month.date.getMonth() === date.getMonth() && month.date.getFullYear() === date.getFullYear())
      ) {
        return month;
      }
    }
    return null;
  }
}
